/**
 * Clear the "Choose Size" prompt state set by ProductForm#promptForVariantSelection.
 */
function clearVariantSelectionPrompt(select) {
  select.closest('.variant-select')?.classList.remove('variant-select--error');
  select.removeAttribute('aria-invalid');
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.outOfStockButton = this.querySelector('#outofstock');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.handleCustomVariantSelect();
      }

      connectedCallback() {
        this.setupStickyButtonsOnScroll();
      }

      disconnectedCallback() {
        if (this._stickyObserver && this._buttonsEl) {
          this._stickyObserver.unobserve(this._buttonsEl);
        }
        if (this._footerObserver) {
          this._footerObserver.disconnect();
        }
        window.removeEventListener('scroll', this._onScrollSticky, { passive: true });
      }

      setupStickyButtonsOnScroll() {
        const buttons = this.querySelector('.product-form__buttons');
        if (!buttons) return;
        this._buttonsEl = buttons;

        if (window.self !== window.top) {
          buttons.classList.remove('position-sticky');
          return;
        }

        if (this._stickyObserver) {
          this._stickyObserver.unobserve(buttons);
        }
        window.removeEventListener('scroll', this._onScrollSticky, { passive: true });

        // Wrap buttons to keep layout height fixed when sticky kicks in
        let wrapper = buttons.parentElement;
        if (wrapper && !wrapper.classList.contains('product-form__buttons-wrapper')) {
          wrapper = document.createElement('div');
          wrapper.className = 'product-form__buttons-wrapper';
          buttons.parentNode?.insertBefore(wrapper, buttons);
          wrapper.appendChild(buttons);
        }

        const ORIGINAL_PLACE_VISIBLE_TOP = 150;
        this._onScrollSticky = () => {
          if (!buttons.classList.contains('position-sticky')) return;
          if (this._buttonsNaturalTop == null) return;
          const wouldBeTop = this._buttonsNaturalTop - window.scrollY;
          if (wouldBeTop >= 0 && wouldBeTop < ORIGINAL_PLACE_VISIBLE_TOP) {
            buttons.classList.remove('position-sticky');
            if (wrapper) wrapper.style.height = ''; // Reset height
            this._buttonsNaturalTop = buttons.getBoundingClientRect().top + window.scrollY;
          }
        };
        window.addEventListener('scroll', this._onScrollSticky, { passive: true });

        this._stickyObserver = new IntersectionObserver(
          (entries) => {
            const [entry] = entries;
            const top = entry.boundingClientRect.top;
            if (top < 0) {
              const rect = buttons.getBoundingClientRect();
              // Lock the wrapper height BEFORE applying fixed position
              if (wrapper) wrapper.style.height = `${rect.height}px`;

              buttons.classList.add('position-sticky');
              this._buttonsNaturalTop = window.scrollY + top;
            }
          },
          { threshold: 0 }
        );
        this._stickyObserver.observe(buttons);
        if (!buttons.classList.contains('position-sticky')) {
          this._buttonsNaturalTop = buttons.getBoundingClientRect().top + window.scrollY;
        }

        if (this._footerObserver) {
          this._footerObserver.disconnect();
        }
        const footerEl = document.querySelector('.footer') || document.querySelector('footer');
        if (footerEl) {
          this._footerObserver = new IntersectionObserver(
            (entries) => {
              buttons.classList.toggle('is-hidden-by-footer', entries[0].isIntersecting);
            },
            { threshold: 0 }
          );
          this._footerObserver.observe(footerEl);
        }
      }

      handleCustomVariantSelect() {
        const ELM_selectOption = document.querySelectorAll('[name="select-variant"]');
        if (!ELM_selectOption) return;
        ELM_selectOption.forEach(select => {
          select.addEventListener('change', e => {
            const selectedOption = e.target?.options[e.target.selectedIndex];
            const variantId = e?.target?.value;
            const ELM_card = select.closest('.card-product-wrapper');
            if (!ELM_card) return;
            const ELM_productForm = ELM_card.querySelector('product-form');
            const ELM_price = ELM_card.querySelector('.price-item');
            const price = selectedOption?.dataset?.price;
            // `variantId || ''` — re-picking the "Choose Size" placeholder must
            // clear the id again, not leave the previous size posted.
            if (ELM_productForm) {
              const idInput = ELM_productForm.querySelector('[name=id]');
              if (idInput) idInput.value = variantId || '';
            }
            if (ELM_price && price) {
              ELM_price.innerHTML = price;
            }
            if (variantId) clearVariantSelectionPrompt(select);
          });
        });
      }

      /**
       * Upsell cards ("Pairs Perfectly With") open on a "Choose Size"
       * placeholder. If ADD is pressed with nothing picked, open the size
       * dropdown instead of adding to cart. Returns true when the submit was
       * intercepted. No-op on the PDP form — only the horizontal card snippet
       * renders `.card-product-wrapper`.
       */
      promptForVariantSelection() {
        const select = this.closest('.card-product-wrapper')?.querySelector('[name="select-variant"]');
        if (!select || select.value) return false;

        select.closest('.variant-select')?.classList.add('variant-select--error');
        select.setAttribute('aria-invalid', 'true');
        // preventScroll — the card lives in a Swiper slide; letting the browser
        // scroll it into view fights the carousel's transform. The select sits
        // beside the ADD button that was just clicked, so it is already visible.
        select.focus({ preventScroll: true });

        // showPicker() is the only way to open a native <select> from script.
        // It throws where unsupported (older Safari/Firefox) and without
        // transient user activation (e.g. an Enter-key submit) — the focus
        // above plus the error outline is the fallback in those cases.
        try {
          select.showPicker();
        } catch (e) {
          /* focused + highlighted above */
        }
        return true;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (this.promptForVariantSelection()) return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('dawn-hidden');
              this.error = true;
              return;
            }

            try {
              this.pushAddToCartToDataLayer(response, formData);
            } catch (e) {
              console.warn('[product-form] add_to_cart dataLayer push failed', e);
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      /**
       * Push a GA4 `add_to_cart` event to the GTM dataLayer for underlay-upsell adds.
       */
      pushAddToCartToDataLayer(response, formData) {
        if (typeof window === 'undefined') return;

        // Underlay path only. A plain single-variant add already emits `add_to_cart`
        // through the Google-channel Web Pixel, so pushing there too would double-
        // count. The underlay is posted by snippets/pdp-underlay.liquid as a second
        // line, `items[1][id]` — absent from FormData when no underlay is selected.
        const addonVariantId = formData?.get('items[1][id]');
        if (!addonVariantId) return;

        // Verified 2026-08-06: `/cart/add.js` returns ONLY the line posted as the
        // top-level `id` (the rug) — a bare line-item object, no `items` wrapper —
        // even though both products do land in the cart. So the rug comes from the
        // response and the underlay from the map the snippet publishes.
        const mainLine = response?.product_id ? response : null;
        if (!mainLine) return;

        const productInfo = this.closest('product-info');
        const storeCode = productInfo?.dataset.store?.trim() || '';
        const currency =
          productInfo?.dataset.currency?.trim() || window.Shopify?.currency?.active || '';

        // Google's Shopify feed id format, same as the pixel and as the
        // `variant_change` event in assets/product-info.js.
        const feedId = (productId, variantId) => `shopify_${storeCode}_${productId}_${variantId}`;

        // `price` (not `final_price`) to match the Web Pixel, which reports the
        // undiscounted variant price — keeping the underlay path consistent with
        // every other add_to_cart rather than mixing discounted and list values.
        const items = [
          {
            item_id: feedId(mainLine.product_id, mainLine.variant_id || mainLine.id),
            item_name: mainLine.product_title || mainLine.title || '',
            item_variant: mainLine.variant_title || '',
            price: mainLine.price / 100,
            quantity: mainLine.quantity || 1,
          },
        ];

        const addon = window.pdpUnderlayData?.[addonVariantId];
        if (addon) {
          items.push({
            item_id: feedId(addon.product_id, addonVariantId),
            item_name: addon.product_title || '',
            item_variant: addon.variant_title || '',
            price: addon.price / 100,
            // pdp-underlay posts no `items[1][quantity]`, so Shopify defaults to 1.
            quantity: 1,
          });
        } else {
          console.warn('[product-form] no pdpUnderlayData entry for variant', addonVariantId);
        }

        const value =
          Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;

        window.dataLayer = window.dataLayer || [];
        // Clear the previous event's ecommerce object — the dataLayer merges pushes,
        // so without this a later event inherits this one's items.
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'add_to_cart',
          ecommerce: {
            value,
            currency,
            items,
          },
        });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text, variant) {
        const ELM_similarProducts = document.querySelector('#pdp-sp');
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
          this.outOfStockButton?.classList?.remove('hidden');
          ELM_similarProducts?.classList?.remove('hidden');
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.innerHTML = `${window.variantStrings.addToCart} <span class="price-item">$${(variant.price / 100.0).toFixed(2)}</span>`;
          this.outOfStockButton?.classList?.add('hidden');
          ELM_similarProducts?.classList?.add('hidden');
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
